import { cloneDeep } from "lodash";
import { AlarmProfile } from "./alarm-profile";
import { v4 as uuidv4 } from "uuid";
import { Notifications } from "./notifications";
import { Criteria } from "./criteria";
import { Alarm } from "./alarm";
import { EMail } from "./email";
import { Condition } from "./condition";

interface AlarmProfilesParams {
  alarmProfiles: AlarmProfile[];
}

export class AlarmProfiles {
  private alarmProfiles: AlarmProfile[];
  private observers: Set<() => void>;

  constructor({ alarmProfiles }: AlarmProfilesParams) {
    this.observers = new Set();

    if (alarmProfiles) {
      this.setAlarmProfiles(alarmProfiles);
    } else {
      this.setAlarmProfiles([]);
    }
  }

  getAlarmProfiles() {
    return this.alarmProfiles;
  }

  getActiveAlarmProfile() {
    return this.alarmProfiles.find((alarmProfile) => alarmProfile.getActive());
  }

  setAlarmProfiles(alarmProfiles: AlarmProfile[]) {
    this.alarmProfiles = alarmProfiles;
    this.notifyObservers();
  }

  deleteAlarmProfile(id: string) {
    // this method deletes the profile and, if the deleted is active, sets the previous one active
    // (or the next if the deleted one is the first element of the array)
    const index = this.alarmProfiles.findIndex(
      (alarmProfile) => alarmProfile.getId() === id,
    );

    if (index === -1) return;

    const isActive = this.alarmProfiles[index].getActive();

    const newProfiles = this.alarmProfiles.filter(
      (alarmProfile) => alarmProfile.getId() !== id,
    );

    if (isActive && newProfiles.length > 0) {
      const newIndex = index === 0 ? 0 : index - 1;
      newProfiles[newIndex].setActive(true);
    }

    this.setAlarmProfiles(newProfiles);
  }

  duplicateAlarmProfile(id: string) {
    const alarmProfile = this.alarmProfiles.find(
      (alarmProfile) => alarmProfile.getId() === id,
    );

    if (alarmProfile) {
      const duplicatedNotifications = cloneDeep(
        alarmProfile.getNotifications(),
      );
      const duplicatedAlarms = cloneDeep(alarmProfile.getAlarms());

      const duplicatedAlarmProfile = new AlarmProfile(
        alarmProfile.getSourceId(),
        alarmProfile.getClientId(),
        false,
        uuidv4(),
        `${alarmProfile.getName()} (copy)`,
        `${alarmProfile.getDescription()} (copy)`,
        duplicatedNotifications,
        duplicatedAlarms,
        true,
      );

      this.setAlarmProfiles([...this.alarmProfiles, duplicatedAlarmProfile]);
    }
  }

  addObserver(observer: () => void) {
    this.observers.add(observer);
  }

  removeObserver(observer: () => void) {
    this.observers.delete(observer);
  }

  notifyObservers() {
    this.observers.forEach((observer) => observer());
  }

  toJSON() {
    return this.alarmProfiles.map((alarmProfile) => alarmProfile.toJSON());
  }

  clone(): AlarmProfiles {
    const clonedAlarmProfiles = this.alarmProfiles.map((profile) => {
      const email = profile.getNotifications().getEmail();
      const clonedNotifications = new Notifications(
        new EMail(
          email.getActive(),
          email.getSubjectTemplate(),
          email.getBodyTemplate(),
          [...email.getRecipients()],
        ),
      );

      const clonedAlarms = profile.getAlarms().map((alarm) => {
        const criteria = alarm.getCriteria();
        const clonedCriteria = new Criteria(
          criteria.getLogicalOperator(),
          criteria
            .getConditions()
            .map(
              (condition) =>
                new Condition(
                  condition.getUserInput(),
                  condition.getCaseSensitive(),
                  condition.getOperator(),
                ),
            ),
        );

        return new Alarm(
          alarm.getOurNode(),
          clonedCriteria,
          alarm.getActive(),
          alarm.getType(),
          alarm.getDataType(),
        );
      });

      return new AlarmProfile(
        profile.getSourceId(),
        profile.getClientId(),
        profile.getActive(),
        profile.getId(),
        profile.getName(),
        profile.getDescription(),
        clonedNotifications,
        clonedAlarms,
        profile.getJustCreated(),
      );
    });

    return new AlarmProfiles({ alarmProfiles: clonedAlarmProfiles });
  }

  isEqualTo(other: AlarmProfiles): boolean {
    return JSON.stringify(this.toJSON()) === JSON.stringify(other.toJSON());
  }
}
